import { Timestamp } from "firebase/firestore";
import { useEffect, useState, type ReactNode } from "react";
import { Button } from "risa-oasis-ui_v2";
import { PharmaStpFileModel } from "../../../../data-model/nycbsPharmaOrder";
import { PocAssignee, PocAssigneeLabels } from "../../../../enums/pocAssignee";
import {
  getEntryLoggedDate,
  getEntryPoc,
  getEntryReason,
  getEntryRequired,
} from "../../../../pages/callingWorklist/services/callingHistoryFields";
import {
  CallingHistoryLog,
  fetchCallingHistoryLogs,
} from "../../../../pages/callingWorklist/services/callingHistoryService";

/**
 * Resolve a POC identifier (Firebase UID) to the corresponding display name.
 *
 * @param pocId - The raw POC ID stored in Firestore
 * @returns The human-readable name, or the original ID if no mapping exists
 */
const getPocDisplayName = (pocId: string): string => {
  return PocAssigneeLabels[pocId as PocAssignee] ?? pocId;
};

interface HistoryTabProps {
  data: PharmaStpFileModel;
  onClose?: () => void;
  /**
   * When true, omits the bottom Cancel bar (e.g. when embedded in SFTP/CMM modal
   * where the parent already provides dismiss actions).
   */
  hideFooter?: boolean;
  /**
   * When true, keeps each entry's fields in one column — used inside the narrow
   * left pane of the non-EPA modal.
   */
  singleColumn?: boolean;
}

/**
 * Format a Firestore Timestamp or date string into MM/DD/YYYY display format.
 *
 * @input value - Timestamp, Date, or date string to format
 * @return Formatted date string or "--" if the value is falsy
 */
const formatDisplayDate = (value: Timestamp | Date | string): string => {
  if (!value) return "--";
  const date =
    value instanceof Timestamp
      ? value.toDate()
      : value instanceof Date
        ? value
        : new Date(value);
  if (isNaN(date.getTime())) return String(value);
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

/**
 * Renders a compact label/value cell for the calling history grid.
 *
 * @param props.label - Short field label shown above the value
 * @param props.value - Primary text (or placeholder) for the field
 * @param props.className - Optional extra classes on the wrapper (e.g. column span)
 */
const HistoryField = (props: {
  label: string;
  value: ReactNode;
  className?: string;
}) => (
  <div className={props.className ?? ""}>
    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-primaryGray-6">
      {props.label}
    </p>
    <p className="text-sm font-medium leading-snug text-primaryGray-1">
      {props.value}
    </p>
  </div>
);

/**
 * History tab displaying past calling summary entries fetched from Firestore at
 * /cmm_workflow/{identifier}/nonEPA_logs/{channel}/logs plus the legacy
 * /cmm_workflow/{identifier}/calling_history_logs collection.
 * Each entry is shown as a card with a header (call date), a two-column field grid,
 * and an optional notes section for calling reason and comments.
 *
 * @param props - Tab props
 * @param props.data - Order row; `identifier` is used to load calling history logs
 * @param props.onClose - Called when the user clicks Cancel (ignored when `hideFooter` is true)
 * @param props.hideFooter - When true, hides the bottom Cancel bar (for embedding in other shells)
 */
const HistoryTab = (props: HistoryTabProps) => {
  const [logs, setLogs] = useState<CallingHistoryLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const identifier = props.data?.identifier;
    if (!identifier) {
      setLoading(false);
      return;
    }

    fetchCallingHistoryLogs(identifier).then((data) => {
      setLogs(data);
      setLoading(false);
    });
  }, [props.data?.identifier]);

  const scrollPadding = props.hideFooter ? "px-6 py-5" : "px-5 py-5";
  const fieldGridColumns = props.singleColumn
    ? "grid-cols-1"
    : "grid-cols-1 sm:grid-cols-2";
  const fullWidthField = props.singleColumn ? "" : "sm:col-span-2";

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className={`flex-1 overflow-y-auto ${scrollPadding}`}>
        {loading && (
          <div className="flex flex-col items-center justify-center gap-2 py-16">
            <div
              className="h-8 w-8 animate-spin rounded-full border-2 border-primaryGray-12 border-t-primaryGray-4"
              aria-hidden
            />
            <p className="text-sm text-primaryGray-6">
              Loading calling history…
            </p>
          </div>
        )}

        {!loading && logs.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-primaryGray-15 bg-white/80 py-14 text-center">
            <p className="text-sm font-medium text-primaryGray-1">
              No calling history yet
            </p>
            <p className="mt-1 max-w-sm text-xs text-primaryGray-6">
              Saved calling summaries for this order will show up here.
            </p>
          </div>
        )}

        {!loading && logs.length > 0 && (
          <ul className="flex list-none flex-col gap-4 p-0">
            {logs.map((entry, index) => (
              <li
                key={entry.id ?? index}
                className="overflow-hidden rounded-lg border border-primaryGray-15 bg-white shadow-sm"
              >
                <div className="flex items-center justify-between gap-3 border-b border-primaryGray-15 bg-primaryGray-16 px-4 py-3">
                  <span className="text-xs font-semibold uppercase tracking-wide text-primaryGray-6">
                    {entry.mode === "fax" ? "Faxed on" : "Called on"}
                  </span>
                  <span className="text-sm font-semibold text-primaryGray-1">
                    {formatDisplayDate(getEntryLoggedDate(entry))}
                  </span>
                </div>

                <div className={`grid gap-4 p-4 ${fieldGridColumns}`}>
                  <HistoryField
                    label="POC"
                    value={getPocDisplayName(getEntryPoc(entry))}
                  />
                  <HistoryField label="Case ID" value={entry.case_id || "—"} />
                  {entry.mode === "fax" ? (
                    <>
                      <HistoryField
                        label="Sent to"
                        value={entry.sent_to || "—"}
                      />
                      <HistoryField
                        label="Fax"
                        value={entry.fax_number || "—"}
                      />
                    </>
                  ) : (
                    <>
                      <HistoryField
                        label="Rep name"
                        value={entry.rep_name || "—"}
                      />
                      <HistoryField
                        label="Phone"
                        value={entry.phone_number || "—"}
                      />
                    </>
                  )}
                  <HistoryField
                    label="Calling status"
                    value={entry.case_status || "—"}
                  />
                  <HistoryField
                    label="Response status"
                    value={entry.response_status || "—"}
                  />
                  {getEntryRequired(entry) ? (
                    <HistoryField
                      label="Outreach required"
                      value={
                        <span className="capitalize">
                          {getEntryRequired(entry)}
                        </span>
                      }
                      className={fullWidthField}
                    />
                  ) : null}
                </div>

                {(getEntryReason(entry) || entry.comment) && (
                  <div className="space-y-3 border-t border-primaryGray-15 bg-primaryGray-16 px-4 py-3">
                    {getEntryReason(entry) ? (
                      <div>
                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-primaryGray-6">
                          Outreach reason
                        </p>
                        <p className="whitespace-pre-wrap text-sm leading-relaxed text-primaryGray-3">
                          {getEntryReason(entry)}
                        </p>
                      </div>
                    ) : null}
                    {entry.comment ? (
                      <div>
                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-primaryGray-6">
                          Comment
                        </p>
                        <p className="whitespace-pre-wrap text-sm leading-relaxed text-primaryGray-3">
                          {entry.comment}
                        </p>
                      </div>
                    ) : null}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {!props.hideFooter && (
        <div className="flex items-center border-t border-primaryGray-15 px-5 py-4">
          <Button
            buttonType="secondary"
            size="medium"
            onClick={() => props.onClose?.()}
            disabled={false}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};

export default HistoryTab;
